import { Injectable } from '@angular/core';
import { LoadAggregatedJobsData } from '@collections/aggregations';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, mergeMap, pluck, switchMap, withLatestFrom } from 'rxjs/operators';

import { assignCandidatesLoad, getFiltersSetOnAssignedCandidates } from '@modules/assigns/store';
import { getJobPageCandidatesState, jobCandidatesLoad } from '@modules/job-page/store';
import { DangerAlert, SuccessAlert } from '@shared/alert';
import { HcmJobPostService } from '@shared/invite-to-apply/services/hcm-job-post.service';
import { InviteToApplyService } from '@shared/invite-to-apply/services/invite-to-apply.service';
import {
  hcmJobPostServiceFail,
  hcmJobPostServiceSuccess,
  hcmJobPostShowError,
  inviteToApplyUpdateSuccess,
  sendInviteToApply,
  sendInviteToApplyFail,
  sendInviteToApplySuccess,
  sendInviteToApplySuccessClearMatchedCache,
  sendJobPostUrlRequest,
} from '@shared/invite-to-apply/store/invite-to-apply.actions';
import { InviteToApply } from '@shared/models/invite-to-apply';
import { ApiParams, JobService } from '@shared/services';
import { WAITING_FOR_APPLICATION_STATUS } from '@shared/utils';

export interface HcmJobPostUrlParams extends ApiParams {
  clientReqId: string;
}

@Injectable()
export class InviteToApplyEffects {
  sendInviteToApply$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sendInviteToApply),
      pluck('inviteToApply'),
      mergeMap((inviteToApply: InviteToApply) => {
        const application = {
          candidateId: inviteToApply.candidateId,
          jobId: inviteToApply.jobId,
          status: WAITING_FOR_APPLICATION_STATUS,
        };
        return this.jobService
          .post({ url: 'candidate-job-application-status', body: application })
          .pipe(
            map(() => inviteToApplyUpdateSuccess({ inviteToApply })),
            catchError((error) => of(sendInviteToApplyFail({ error }))),
          );
      }),
    ),
  );

  sendJobPostUrlRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sendJobPostUrlRequest),
      mergeMap(({ clientReqId, jobId }) => {
        const params: HcmJobPostUrlParams = {
          clientReqId,
        };
        return this.hcmJobPostService.get({ params }).pipe(
          map((response) =>
            hcmJobPostServiceSuccess({
              url: response.url,
              jobId,
            }),
          ),
          catchError((error) => {
            console.error(error);
            return of(hcmJobPostServiceFail({ error, jobId }));
          }),
        );
      }),
    ),
  );

  sendJobPostUrlRequestFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(hcmJobPostShowError),
      map(
        () =>
          new DangerAlert('This candidate cannot be invited right now. Please, contact support.'),
      ),
    ),
  );

  inviteToApplyUpdateSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(inviteToApplyUpdateSuccess),
      mergeMap(({ inviteToApply }) =>
        this.inviteService.post({ body: inviteToApply }).pipe(
          map((response: InviteToApply) => {
            if (inviteToApply.fromCandidate) {
              return new SuccessAlert('Invitation to apply has been sent.');
            } else {
              return sendInviteToApplySuccessClearMatchedCache({ response });
            }
          }),
          catchError((error) => of(sendInviteToApplyFail({ error }))),
        ),
      ),
    ),
  );

  sendInviteToApplySuccessClearMatchedCache$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sendInviteToApplySuccessClearMatchedCache),
      pluck('response'),
      map((response) => sendInviteToApplySuccess({ response })),
    ),
  );

  sendInviteToApplySuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sendInviteToApplySuccess),
      withLatestFrom(
        this.store.select(getJobPageCandidatesState),
        this.store.select(getFiltersSetOnAssignedCandidates),
      ),
      switchMap(([, totalMatchesCandidates, assignedStatuses]) => {
        const offsetValue =
          totalMatchesCandidates.numberOfElements > 1
            ? totalMatchesCandidates.number
            : this.loadPreviousPageIfNoMatchesAfterInvite(totalMatchesCandidates.number);
        return [
          new SuccessAlert('Invitation to apply has been sent.'),
          jobCandidatesLoad({
            params: { offset: offsetValue },
          }),
          assignCandidatesLoad({
            params: { offset: 0 },
            filters: assignedStatuses,
          }),
          LoadAggregatedJobsData({
            jobsIds: [],
            updateSingleAggregatedJobData: true,
          }),
        ];
      }),
    ),
  );

  sendInviteToApplyFail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sendInviteToApplyFail),
      map(({ error }) => {
        console.error(error);
        return new DangerAlert('A problem occurred while sending invitation to apply.');
      }),
    ),
  );

  private loadPreviousPageIfNoMatchesAfterInvite(number) {
    return number > 0 ? number - 1 : 0;
  }

  constructor(
    private jobService: JobService,
    private actions$: Actions,
    private inviteService: InviteToApplyService,
    private hcmJobPostService: HcmJobPostService,
    private store: Store<{}>,
  ) {}
}
